import { createServerFn } from "@tanstack/react-start";
import { getRequest } from "@tanstack/react-start/server";
import { createClient } from "@supabase/supabase-js";
import { z } from "zod";

import type { Database } from "@/integrations/supabase/types";

const feedbackSchema = z.object({
  message: z
    .string()
    .trim()
    .min(1, { message: "Please write a message first." })
    .max(5000, { message: "Please keep it under 5,000 characters." }),
  email: z
    .string()
    .trim()
    .email({ message: "That email address doesn't look right." })
    .max(255)
    .optional()
    .or(z.literal("")),
});

// Publishable keys are opaque strings, not JWTs — strip them from the
// Authorization header and send them as `apikey` instead (same approach as
// the generated auth middleware).
function createKeyFetch(key: string): typeof fetch {
  return (input, init) => {
    const headers = new Headers(
      typeof Request !== "undefined" && input instanceof Request ? input.headers : undefined,
    );
    if (init?.headers) new Headers(init.headers).forEach((v, k) => headers.set(k, v));
    if (headers.get("Authorization") === `Bearer ${key}`) headers.delete("Authorization");
    headers.set("apikey", key);
    return fetch(input, { ...init, headers });
  };
}

// See src/integrations/supabase/client.ts's SUPABASE_DB_SCHEMA comment --
// same env var, same default.
// Cast to "public": see src/integrations/supabase/client.ts's comment.
const SUPABASE_DB_SCHEMA = (process.env["SUPABASE_DB_SCHEMA"] || "public") as "public";

function createAnonClient(url: string, key: string) {
  return createClient<Database>(url, key, {
    db: { schema: SUPABASE_DB_SCHEMA },
    global: { fetch: createKeyFetch(key) },
    auth: { storage: undefined, persistSession: false, autoRefreshToken: false },
  });
}

/**
 * Public on purpose: the feedback form is linked from the site footer on
 * every page, including for signed-out visitors. When a bearer token is
 * present (attached automatically for signed-in users) we verify it and link
 * the row to their account; otherwise the row is stored anonymously.
 */
export const submitFeedback = createServerFn({ method: "POST" })
  .inputValidator((input) => feedbackSchema.parse(input))
  .handler(async ({ data }) => {
    const SUPABASE_URL = process.env["SUPABASE_URL"];
    const SUPABASE_PUBLISHABLE_KEY = process.env["SUPABASE_PUBLISHABLE_KEY"];
    if (!SUPABASE_URL || !SUPABASE_PUBLISHABLE_KEY) {
      throw new Error("Feedback is not configured on this server.");
    }

    const request = getRequest();
    const authHeader = request?.headers.get("authorization");
    const token = authHeader?.startsWith("Bearer ") ? authHeader.slice(7) : null;

    const createAuthedClient = (bearer: string) =>
      createClient<Database>(SUPABASE_URL, SUPABASE_PUBLISHABLE_KEY, {
        db: { schema: SUPABASE_DB_SCHEMA },
        global: {
          fetch: createKeyFetch(SUPABASE_PUBLISHABLE_KEY),
          headers: { Authorization: `Bearer ${bearer}` },
        },
        auth: { storage: undefined, persistSession: false, autoRefreshToken: false },
      });

    let userId: string | null = null;
    if (token && token.split(".").length === 3) {
      const { data: claimsData } = await createAuthedClient(token).auth.getClaims(token);
      userId = claimsData?.claims?.sub ?? null;
    }

    // Signed-in rows go through the authenticated client (RLS checks
    // auth.uid() = user_id); anonymous rows through the plain anon client.
    const client =
      userId && token
        ? createAuthedClient(token)
        : createAnonClient(SUPABASE_URL, SUPABASE_PUBLISHABLE_KEY);

    const { error } = await client.from("feedback").insert({
      user_id: userId,
      email: data.email ? data.email : null,
      message: data.message,
    });
    if (error) throw new Error(error.message);

    // Best-effort: the feedback is already saved regardless of whether this
    // succeeds, so a flaky SMTP connection must never surface as a failed
    // submission to the person who just wrote in.
    try {
      const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
      const { data: admins } = await supabaseAdmin
        .from("profiles")
        .select("email")
        .eq("role", "admin")
        .not("email", "is", null);
      const recipients = (admins ?? [])
        .map((a) => a.email)
        .filter((email): email is string => !!email);
      if (recipients.length > 0) {
        const { sendMail } = await import("@/lib/mail.server");
        const from = data.email ? data.email : userId ? `signed-in user (${userId})` : "an anonymous visitor";
        await sendMail(
          recipients,
          "New feedback on Understand Your Genome",
          `From: ${from}\n\n${data.message}`,
        );
      }
    } catch (mailError) {
      console.error("[feedback] failed to notify admins:", mailError);
    }

    return { ok: true };
  });
